import { FormatMask } from './FormatMask';
import { i18n } from '../translate/i18n';

const extractDigits = (value) => (value || '').replace(/\D/g, '');

const isEmailAddress = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const normalizePhoneNumber = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  if (str.startsWith('lid:')) {
    return `lid:${str.slice(4).replace(/\D/g, '')}`;
  }
  return str.replace(/\D/g, '');
};

const isLikelyLidNumber = (digits) => {
  const clean = extractDigits(digits);
  if (!clean) return false;
  if (clean.startsWith('lid:')) return true;
  if (/^569\d{8}$/.test(clean)) return false;
  if (/^56\d{9,10}$/.test(clean) && clean.length <= 12) return false;
  if (/^55\d{10,11}$/.test(clean)) return false;
  if (clean.length >= 8 && clean.length <= 13) {
    if (clean.startsWith('1') && clean.length >= 14) return true;
    return false;
  }
  return clean.length >= 14;
};

const formatChileNumber = (digits) => {
  const clean = extractDigits(digits);
  if (/^569\d{8}$/.test(clean)) {
    return `🇨🇱 +56 ${clean.slice(2, 3)} ${clean.slice(3, 7)} ${clean.slice(7)}`;
  }
  if (/^56\d{9,10}$/.test(clean) && clean.length <= 12) {
    return `🇨🇱 +${clean.slice(0, 2)} ${clean.slice(2)}`;
  }
  return null;
};

const formatSerializedId = (serializedId) => {
  if (!serializedId) return null;

  const cleanNumber = extractDigits(serializedId);

  if (isLikelyLidNumber(cleanNumber)) {
    return null;
  }

  const chileFormatted = formatChileNumber(cleanNumber);
  if (chileFormatted) {
    return chileFormatted;
  }

  const formatMask = new FormatMask();
  const number = serializedId.replace('@c.us', '');

  return formatMask.setPhoneFormatMask(number)?.replace('+55', '🇧🇷');
};

const canFormatNumber = (number) => {
  if (!number) return false;

  const cleanNumber = typeof number === 'string' ? number.replace('@c.us', '') : String(number);
  const digits = extractDigits(cleanNumber);

  if (isLikelyLidNumber(digits)) return false;

  if (/^(569|56)\d{8,9}$/.test(digits)) return true;
  return /^(55|\+55)?\d{10,11}$/.test(cleanNumber.replace(/[^\d+]/g, ''));
};

const safeFormatPhoneNumber = (number, hideNumber = false, isGroup = false) => {
  if (isGroup || !number) return number;

  try {
    const digits = extractDigits(number);

    if (isLikelyLidNumber(digits)) {
      return 'WhatsApp (ID privado)';
    }

    const formattedNumber = formatSerializedId(number);

    if (formattedNumber && hideNumber) {
      if (formattedNumber.includes('🇧🇷')) {
        const parts = formattedNumber.match(/🇧🇷\s*\((\d{2})\)\s*(\d+)-(\d+)/);
        if (parts && parts.length >= 4) {
          const [, ddd, parte1, parte2] = parts;
          return `🇧🇷 (${ddd}) ${parte1.slice(0, -2)}**-**${parte2.slice(-2)}`;
        }
      }

      if (formattedNumber.includes('🇨🇱')) {
        const parts = formattedNumber.match(/🇨🇱 \+56 (\d) (\d{4}) (\d{4})/);
        if (parts && parts.length >= 4) {
          const [, prefix, middle, end] = parts;
          return `🇨🇱 +56 ${prefix} ${middle.slice(0, 2)}** **${end.slice(-2)}`;
        }
      }

      return formattedNumber.slice(0, -6) + '**-**' + number.slice(-2);
    }

    if (formattedNumber) return formattedNumber;

    if (canFormatNumber(number)) {
      const cleanNumber = extractDigits(number);
      const chileFormatted = formatChileNumber(cleanNumber);
      if (chileFormatted) {
        return chileFormatted;
      }

      const formatMask = new FormatMask();
      const manualFormatted = formatMask.setPhoneFormatMask(cleanNumber)?.replace('+55', '🇧🇷');

      if (manualFormatted && hideNumber) {
        const parts = manualFormatted.match(/🇧🇷\s*\((\d{2})\)\s*(\d+)-(\d+)/);
        if (parts && parts.length >= 4) {
          const [, ddd, parte1, parte2] = parts;
          return `🇧🇷 (${ddd}) ${parte1.slice(0, -2)}**-**${parte2.slice(-2)}`;
        }
        return manualFormatted.slice(0, -6) + '**-**' + cleanNumber.slice(-2);
      }

      return manualFormatted || number;
    }

    if (hideNumber && typeof number === 'string' && number.length > 8) {
      return number.slice(0, -6) + '**-**' + number.slice(-2);
    }

    return number;
  } catch (error) {
    console.error('Erro ao formatar número:', error);
    return number;
  }
};

const isLikelyMetaPsid = (value) => {
  const clean = extractDigits(String(value || ""));
  return clean.length >= 10;
};

const getFacebookFallbackName = () =>
  i18n.t("contacts.facebookFallbackName");

const formatContactDisplayName = (contact) => {
  if (!contact) return "";

  const name = (contact.name || "").trim();
  const number = (contact.number || "").trim();
  const fallback = getFacebookFallbackName();

  if (contact.channel === "email" || isEmailAddress(number)) {
    return name || number || contact.email || "";
  }

  if (contact.channel === "facebook") {
    if (
      !name ||
      name === number ||
      name === fallback ||
      name === "Usuario Facebook" ||
      isLikelyMetaPsid(name)
    ) {
      return fallback;
    }
  }

  return name || number || "";
};

export {
  formatSerializedId,
  safeFormatPhoneNumber,
  isLikelyLidNumber,
  isLikelyMetaPsid,
  normalizePhoneNumber,
  formatContactDisplayName
};
export default formatSerializedId;
